import { memo, useCallback, useState } from 'react';
import { StanButton, StanDropdownMenu, StanTag } from '@/components';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import type { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { useModal } from '@/hooks/useModal';
import { formatCurrency, getCurrencySymbol } from '@/lib/currency';
import type { CustomerProfile, UpdateCustomerPayload } from '../types/customer';
import { EditCustomerModal } from './EditCustomerModal';
import { DeleteCustomerModal } from './DeleteCustomerModal';
import { CreateInvoiceModal } from './CreateInvoiceModal';
import { useCustomerActions } from '../queries/useCustomerActions';
import { LuEllipsisVertical } from 'react-icons/lu';
import { stanNotify } from '@/lib';
import { SENTRY_FEATURES } from '@/lib/constants';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useUser } from '@/queries/useUser';
import { useIntegrations } from '@/queries/useIntegrations';
import { isAxiosError } from 'axios';
interface CustomerOverviewCardProps {
    customer: CustomerProfile;
    onCustomerUpdated?: () => void;
    hasActiveSubscription?: boolean;
    onInvoiceSent?: () => void;
}

export const CustomerOverviewCard = memo(function CustomerOverviewCard({
    customer,
    onCustomerUpdated,
    hasActiveSubscription = false,
    onInvoiceSent,
}: CustomerOverviewCardProps) {
    const { push } = useStanRouter();
    const { user } = useUser();
    const { data: integrations } = useIntegrations();
    // Modal states
    const editModal = useModal();
    const deleteModal = useModal();
    const invoiceModal = useModal();
    const [invoiceServerErrors, setInvoiceServerErrors] = useState<Record<string, string[]>>();

    // Customer actions
    const { updateCustomerMutation, deleteCustomerMutation, sendInvoiceMutation } = useCustomerActions(customer.id);

    const dropdownOptions: DropdownMenuOption[] = [
        { value: 'edit', label: 'Edit Customer' },
        { value: 'delete', label: 'Delete Customer' },
    ];

    const handleDropdownSelect = useCallback(
        (value: string) => {
            if (value === 'edit') {
                editModal.open();
            }
            if (value === 'delete') {
                deleteModal.open();
            }
        },
        [editModal, deleteModal]
    );

    // Modal handlers that combine action execution with modal state
    const handleEditSubmitWithModal = useCallback(
        async (payload: UpdateCustomerPayload) => {
            try {
                await updateCustomerMutation.mutateAsync(payload);
                stanNotify({ type: 'success', title: 'Changes saved!', duration: 3000 });
                onCustomerUpdated?.();
                editModal.close();
            } catch (error) {
                if (isAxiosError(error) && error.response?.status === 429) {
                    stanNotify({
                        type: 'error',
                        title: 'Too many requests',
                        text: 'You have reached the limit for updating this customer.',
                        duration: 5000,
                    });
                    return;
                }
                stanNotify({
                    type: 'error',
                    title: 'Unable to update customer',
                    text: 'Unable to update customer. Please try again or contact support.',
                    duration: 5000,
                    error: {
                        message: '[AUDIENCE] failed-to-update-customer',
                        options: {
                            tags: { feature: SENTRY_FEATURES.AUDIENCE },
                            context: { userFanId: customer.id, error },
                        },
                    },
                });
            }
        },
        [updateCustomerMutation, onCustomerUpdated, editModal, customer.id]
    );

    const handleDeleteWithModal = useCallback(async () => {
        try {
            await deleteCustomerMutation.mutateAsync();
            stanNotify({ type: 'success', title: 'Customer deleted', duration: 3000 });
            deleteModal.close();
            push('/audience');
        } catch (error) {
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: 'Unable to delete customer. Please try again or contact support.',
                duration: 5000,
                error: {
                    message: '[AUDIENCE] failed-to-delete-customer',
                    options: {
                        tags: { feature: SENTRY_FEATURES.AUDIENCE },
                        context: { userFanId: customer.id, error },
                    },
                },
            });
        }
    }, [deleteCustomerMutation, customer.id, deleteModal, push]);

    const handleSendInvoiceWithModal = useCallback(
        async (payload: { description: string; amount: number }) => {
            // Clear previous errors when submitting
            setInvoiceServerErrors(undefined);
            try {
                await sendInvoiceMutation.mutateAsync(payload);
                stanNotify({
                    type: 'success',
                    title: 'Invoice sent',
                    text: `Invoice sent to ${customer.fullName}`,
                    duration: 3000,
                });
                invoiceModal.close();
                setInvoiceServerErrors(undefined);
                onInvoiceSent?.();
            } catch (error) {
                let message = 'Unable to send invoice. Please try again or contact support.';
                let errors: Record<string, string[]> | undefined;

                if (isAxiosError(error) && error.response?.data) {
                    const errorData = error.response.data;
                    message = errorData.message || message;
                    errors = errorData.errors;
                }

                // If we have field-specific errors, store them for the modal to display
                if (errors && Object.keys(errors).length > 0) {
                    setInvoiceServerErrors(errors);
                    // Still show a notification with the general message
                    stanNotify({
                        type: 'error',
                        title: 'Unable to send invoice',
                        text: message,
                        duration: 5000,
                    });
                } else {
                    stanNotify({
                        type: 'error',
                        title: 'Unable to send invoice',
                        text: message,
                        duration: 5000,
                        error: {
                            message: '[AUDIENCE] failed-to-send-invoice',
                            options: {
                                tags: { feature: SENTRY_FEATURES.AUDIENCE },
                                context: { userFanId: customer.id, error },
                            },
                        },
                    });
                }
            }
        },
        [sendInvoiceMutation, invoiceModal, customer.id, customer.fullName, onInvoiceSent]
    );

    const canSendInvoice = integrations?.['stripe-connect']?.status;
    const formattedTotalSpent = formatCurrency(customer.totalSpent ?? 0, user?.data.currency ?? 'USD');

    return (
        <div className='flex flex-col gap-2'>
            <h2 className='text-dark'>Overview</h2>
            <div className='shadow-nucleus-elevation-medium mt-4 rounded-lg bg-white'>
                <div className='flex items-start justify-between p-4 md:flex-row'>
                    <div className=''>
                        <div className='text-gray pb-1'>Name</div>
                        <div className='flex flex-wrap items-center gap-2'>
                            <h3 data-test-id='customer-name' className='text-brand-intense m-0 mr-2 truncate'>
                                {customer.fullName}
                            </h3>
                            {customer.fanTags?.map(tag => (
                                <StanTag key={tag} label={tag} className='bg-brand-subtle text-brand-main' />
                            )) || []}
                        </div>
                    </div>
                    <div className='flex items-center'>
                        {canSendInvoice && (
                            <StanButton
                                name='customer-details-send-invoice-button'
                                data-test-id='send-invoice'
                                variant='outline'
                                onClick={invoiceModal.open}
                                size='sm'
                            >
                                Send Invoice
                            </StanButton>
                        )}
                        <DropdownProvider
                            dropdownId='customer-overview-actions'
                            dropdownName='customer-overview-actions'
                        >
                            <StanDropdownMenu
                                id='customer-dropdown'
                                name='customer-dropdown'
                                options={dropdownOptions}
                                onSelect={({ value }) => handleDropdownSelect(value)}
                                rounded
                            >
                                <LuEllipsisVertical className='text-gray h-6 w-12' />
                            </StanDropdownMenu>
                        </DropdownProvider>
                    </div>
                </div>

                <div className='border-disabled grid grid-cols-1 border-t md:grid-cols-2 lg:grid-cols-4'>
                    <div className='border-disabled border-b p-4 md:m-4 md:border-r md:border-b-0 md:p-0'>
                        <div className='text-gray pb-1'>Email</div>
                        <p
                            data-test-id='customer-email'
                            className='text-brand-intense caption-1-bold m-0 w-full truncate'
                        >
                            {customer.email}
                        </p>
                    </div>
                    <div className='border-disabled border-b p-3 md:m-3 md:border-r md:border-b-0 md:p-0'>
                        <div className='text-gray pb-1'>Spent</div>
                        <p data-test-id='customer-spent' className='text-brand-intense caption-1-bold m-0 truncate'>
                            {formattedTotalSpent}
                        </p>
                    </div>
                    <div className='border-disabled border-b p-3 md:m-3 md:border-r md:border-b-0 md:p-0'>
                        <div className='text-gray pb-1'>Since</div>
                        <p data-test-id='customer-since' className='text-brand-intense caption-1-bold m-0 truncate'>
                            {customer.fanSince}
                        </p>
                    </div>
                    <div className='p-3'>
                        <div className='text-gray caption-1-regular truncate pb-1'>Phone Number</div>
                        <p data-test-id='customer-phone' className='text-brand-intense caption-1-bold m-0 truncate'>
                            {customer.phoneNumber}
                        </p>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <EditCustomerModal
                isOpen={editModal.isOpen}
                customer={customer}
                onClose={editModal.close}
                onSubmit={handleEditSubmitWithModal}
                isLoading={updateCustomerMutation.isPending}
            />

            <DeleteCustomerModal
                isOpen={deleteModal.isOpen}
                customerName={customer.fullName}
                hasActiveSubscription={hasActiveSubscription}
                onClose={deleteModal.close}
                onConfirm={handleDeleteWithModal}
                isLoading={deleteCustomerMutation.isPending}
            />

            <CreateInvoiceModal
                isOpen={invoiceModal.isOpen}
                currencySymbol={getCurrencySymbol(user?.data.currency ?? 'USD')}
                onClose={() => {
                    invoiceModal.close();
                    setInvoiceServerErrors(undefined);
                }}
                onSubmit={handleSendInvoiceWithModal}
                isLoading={sendInvoiceMutation.isPending}
                serverErrors={invoiceServerErrors}
            />
        </div>
    );
});
